import { FileText, FolderOpen, Loader2, Upload, X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { UploadConflictDialog } from '@/components/documents/UploadConflictDialog'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { lakeApi } from '@/services/api/lake'
import { AUDIO_FILE_SUFFIXES } from '@/utils/audio-playback'
import { findDuplicateTargetPaths } from '@/utils/upload-conflicts'

interface UploadedFile {
  file: File
  id: string
  relativePath: string | null
  progress: number
  status: 'pending' | 'uploading' | 'uploaded' | 'error'
  error?: string
  documentId?: string
}

export interface UploadDestination {
  labelId: string
  labelName: string
  labelColor: string | null
  folderPath: string | null
}

interface LakeUploadProps {
  destination: UploadDestination | null
  onSelectDestination: () => void
  onUploadComplete?: () => void
}

const SUPPORTED_TYPES = [
  '.pdf',
  '.docx',
  '.doc',
  '.xlsx',
  '.xls',
  '.csv',
  '.txt',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gpx',
  // Audio formats routed through the audio_transcription_lake pipeline.
  ...AUDIO_FILE_SUFFIXES,
]

const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

export function LakeUpload({
  destination,
  onSelectDestination,
  onUploadComplete,
}: LakeUploadProps) {
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  // Non-empty while the same-name conflict prompt is shown (SP-357).
  const [conflicts, setConflicts] = useState<string[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  // Add files to list
  const addFiles = useCallback((newFiles: File[], _fromFolder = false) => {
    const validFiles: UploadedFile[] = []
    const errors: string[] = []

    for (const file of newFiles) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File too large (max 100MB)`)
        continue
      }

      // Check file type
      const ext = `.${file.name.split('.').pop()?.toLowerCase()}`
      if (!SUPPORTED_TYPES.includes(ext)) {
        errors.push(`${file.name}: Unsupported file type`)
        continue
      }

      // Extract relative path for folder uploads
      const webkitPath = (file as File & { webkitRelativePath?: string }).webkitRelativePath
      const relativePath = webkitPath || null

      validFiles.push({
        file,
        id: Math.random().toString(36).substr(2, 9),
        relativePath,
        progress: 0,
        status: 'pending',
      })
    }

    if (errors.length > 0) {
      toast.error('Some files were skipped', {
        description: errors.join('\n'),
      })
    }

    setFiles(prev => [...prev, ...validFiles])
  }, [])

  // Handle file drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setDragActive(false)

      const droppedFiles = Array.from(e.dataTransfer.files)
      addFiles(droppedFiles)
    },
    [addFiles]
  )

  // Handle file input (standard files)
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const selectedFiles = Array.from(e.target.files)
        addFiles(selectedFiles)
      }
    },
    [addFiles]
  )

  // Handle folder input (webkitdirectory)
  const handleFolderInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const selectedFiles = Array.from(e.target.files)
        addFiles(selectedFiles, true)
      }
    },
    [addFiles]
  )

  // Remove file
  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  // Destination-relative path for a file — the same string used to build its
  // s3_key on the backend (folder path + filename, or just filename).
  const buildRelativePath = (fileItem: UploadedFile): string | undefined => {
    if (destination?.folderPath) {
      const tail = fileItem.relativePath || fileItem.file.name
      return `${destination.folderPath}/${tail}`
    }
    return fileItem.relativePath || undefined
  }

  const runUpload = async (replace: boolean) => {
    if (!destination) return

    setUploading(true)

    let successCount = 0
    let errorCount = 0

    for (const fileItem of files) {
      if (fileItem.status === 'uploaded') {
        successCount++
        continue
      }

      // Update status
      setFiles(prev =>
        prev.map(f => (f.id === fileItem.id ? { ...f, status: 'uploading', progress: 0 } : f))
      )

      try {
        const relativePath = buildRelativePath(fileItem)
        const response = await lakeApi.uploadFile(
          destination.labelId,
          fileItem.file,
          relativePath,
          replace
        )

        // Mark as uploaded
        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? { ...f, status: 'uploaded', progress: 100, documentId: response.document_id }
              : f
          )
        )

        successCount++
      } catch (error: any) {
        console.error(`Upload failed for ${fileItem.file.name}:`, error)
        setFiles(prev =>
          prev.map(f =>
            f.id === fileItem.id
              ? {
                  ...f,
                  status: 'error',
                  error: error.data?.detail || error.message || 'Upload failed',
                }
              : f
          )
        )
        errorCount++
      }
    }

    setUploading(false)

    if (errorCount === 0) {
      toast.success('Upload complete', {
        description: `${successCount} ${successCount === 1 ? 'file' : 'files'} uploaded successfully`,
      })
    } else {
      toast.warning('Upload completed with errors', {
        description: `${successCount} succeeded, ${errorCount} failed`,
      })
    }

    if (successCount > 0 && onUploadComplete) {
      onUploadComplete()
    }

    // Clear successfully uploaded files after short delay
    if (successCount > 0) {
      setTimeout(() => {
        setFiles(prev => prev.filter(f => f.status !== 'uploaded'))
      }, 2000)
    }
  }

  // Upload all files
  const uploadAll = async () => {
    if (files.length === 0) {
      toast.error('No files selected', {
        description: 'Please add files to upload',
      })
      return
    }

    if (!destination) {
      toast.error('No destination selected', {
        description: 'Select a scope above',
      })
      return
    }

    const pending = files.filter(f => f.status !== 'uploaded')
    const targetPaths = pending.map(f => buildRelativePath(f) ?? f.file.name)

    // Two staged files targeting the same path can't both land — the server
    // dedupes the preflight and the second would only fail with a per-file 409
    // mid-upload. Block up front; replace can't disambiguate same-batch dupes.
    const duplicates = findDuplicateTargetPaths(targetPaths)
    if (duplicates.length > 0) {
      toast.error('Duplicate file names in this selection', {
        description: `Rename or remove before uploading: ${duplicates.join(', ')}`,
      })
      return
    }

    // Preflight: same-name collisions → prompt to replace-all or cancel
    // (SP-357). A failed check falls back to a normal upload — the backend
    // still guards same-name collisions with a 409.
    try {
      const { conflicts: found } = await lakeApi.checkUploadConflicts(
        destination.labelId,
        targetPaths
      )
      if (found.length > 0) {
        setConflicts(found)
        return
      }
    } catch (error) {
      console.error('Conflict check failed:', error)
    }

    await runUpload(false)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getDisplayPath = (fileItem: UploadedFile) => {
    if (fileItem.relativePath) {
      return fileItem.relativePath
    }
    return fileItem.file.name
  }

  return (
    <div className="bg-background p-4 md:p-6">
      <div className="mx-auto space-y-6">
        {/* Header */}
        <div>
          {/* The sheet already titles itself on the phone. */}
          <h2 className="text-2xl font-semibold text-foreground mb-2 max-md:hidden">
            Upload Files
          </h2>
          <p className="text-sm text-muted-foreground">
            Upload files or folders to a data lake label for automatic processing and indexing
          </p>
        </div>

        {/* Destination */}
        {destination ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted border border-border">
            <FolderOpen className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground mb-0.5">Upload destination</div>
              <div className="text-sm font-mono text-foreground truncate">
                lake /{' '}
                <span style={{ color: destination.labelColor || undefined }}>
                  {destination.labelName}
                </span>
                {destination.folderPath && ` / ${destination.folderPath}`}
                {' /'}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSelectDestination}
              className="shrink-0 text-xs text-neutral-400 hover:text-white"
            >
              Change
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={onSelectDestination}
            className="w-full justify-start gap-2 border-neutral-700 text-neutral-400 hover:text-white hover:border-neutral-500"
          >
            <FolderOpen className="h-4 w-4 shrink-0" />
            Select destination folder...
          </Button>
        )}

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors md:p-8 ${
            dragActive
              ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/10'
              : 'border-border bg-muted/30'
          }`}
          onDragEnter={e => {
            e.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={e => {
            e.preventDefault()
            setDragActive(false)
          }}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-lg font-medium text-foreground mb-2">
            Drag & Drop Files or Folders Here
          </p>
          <p className="text-sm text-muted-foreground mb-4">or</p>
          <div className="flex gap-3 justify-center max-md:flex-wrap">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="">
              <FileText className="h-4 w-4 mr-2" />
              Browse Files
            </Button>
            <Button variant="outline" onClick={() => folderInputRef.current?.click()} className="">
              <FolderOpen className="h-4 w-4 mr-2" />
              Browse Folder
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORTED_TYPES.join(',')}
            onChange={handleFileInput}
            className="hidden"
          />
          <input
            ref={folderInputRef}
            type="file"
            // @ts-expect-error - webkitdirectory is not in standard types
            webkitdirectory=""
            directory=""
            onChange={handleFolderInput}
            className="hidden"
          />
          <p className="text-xs text-muted-foreground/70 mt-4">
            Supported: PDF, DOCX, XLSX, XLS, CSV, TXT, JPG, PNG, WEBP, GPX and audio: MP3, WAV, M4A,
            OGG, OPUS, FLAC, AAC, WebM, 3GP, AMR (max 100MB per file)
          </p>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">
                Selected Files ({files.length})
              </Label>
              {!uploading && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiles([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear All
                </Button>
              )}
            </div>
            <Card className="rounded-md divide-y divide-border p-0">
              {files.map(fileItem => (
                <div key={fileItem.id} className="flex items-center gap-3 p-3 min-w-0">
                  <div className="shrink-0">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-sm font-medium text-foreground truncate max-w-full">
                      {getDisplayPath(fileItem)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(fileItem.file.size)}
                      {fileItem.status === 'uploading' && ' • Uploading...'}
                      {fileItem.status === 'uploaded' && ' • ✓ Uploaded'}
                      {fileItem.status === 'error' && ` • Error: ${fileItem.error}`}
                    </p>
                    {fileItem.status === 'uploading' && (
                      <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                        <div
                          className="bg-purple-500 h-1.5 rounded-full transition-all animate-pulse"
                          style={{ width: '60%' }}
                        />
                      </div>
                    )}
                  </div>
                  {fileItem.status !== 'uploading' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(fileItem.id)}
                      disabled={uploading}
                      className="shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* Upload Button */}
        {files.length > 0 && (
          <div className="flex justify-end">
            <Button
              onClick={uploadAll}
              disabled={files.length === 0 || uploading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                `Upload All (${files.length})`
              )}
            </Button>
          </div>
        )}
      </div>

      <UploadConflictDialog
        conflicts={conflicts}
        onReplace={() => {
          setConflicts([])
          void runUpload(true)
        }}
        onCancel={() => setConflicts([])}
      />
    </div>
  )
}
